import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { BillingPlanIds } from '../../../../enums/billing-plan-ids';
import Global from '../../../../store/global-selectors';
import { useDoesSellerHavePromotionalOffer } from '../components/migrate-plan-modal/hooks/use-does-seller-have-promotional-offer';
import { findAnnualPlanId } from '../utils/plan-utilities';

const DefaultPlanId = BillingPlanIds.Standard2025Q2.toString();

/**
 * Returns an array of recommended billing plan IDs based on priority:
 * 1. Promotional target (highest priority)
 * 2. Backend-controlled recommendedBillingPlanIds
 * 3. Hard-coded defaults for Free/Starter/Trial users
 * @param {object} params
 * @param {string | null} params.currentBillingPlanId
 * @param {boolean} params.showAnnualPlans
 * @param {import('../../../../prop-types/subscription-data-types.d.ts').BillingPlan[]} params.referencePlans
 * @returns {string[]} Array of recommended billing plan IDs
 */
export const useGetRecommendedPlanIds = ({ currentBillingPlanId, showAnnualPlans, referencePlans }) => {
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const isCurrentBillingPlanTrial = subscriptionData?.currentBillingPlan?.billingPlanId?.toString() === BillingPlanIds.Trial.toString();
  const promotionUpgrade = useSelector(Global.selectSellerPromotionUpgrade);
  const targetBillingPlanId = promotionUpgrade?.targetBillingPlan?.billingPlanId;
  const hasPromotionalOffer = useDoesSellerHavePromotionalOffer();

  return useMemo(() => {
    // Priority 1: Promotional target (only if offer is available and not accepted)
    if (targetBillingPlanId && hasPromotionalOffer) {
      return [targetBillingPlanId];
    }

    // Priority 2: Backend-controlled recommendations (convert numbers to strings)
    if (subscriptionData?.recommendedBillingPlanIds?.length > 0) {
      return subscriptionData.recommendedBillingPlanIds.map((id) => id.toString());
    }

    // Priority 3: Hard-coded defaults for specific plans
    const shouldShowRecommendation =
      !currentBillingPlanId ||
      currentBillingPlanId === BillingPlanIds.Free2025Q2.toString() ||
      currentBillingPlanId === BillingPlanIds.Starter2025Q2.toString() ||
      currentBillingPlanId === BillingPlanIds.Starter2025Q2Annual.toString() ||
      isCurrentBillingPlanTrial;

    if (!shouldShowRecommendation) {
      return [];
    }

    const defaultId = showAnnualPlans ? (findAnnualPlanId(DefaultPlanId, referencePlans) ?? DefaultPlanId) : DefaultPlanId;
    return [defaultId];
  }, [
    currentBillingPlanId,
    hasPromotionalOffer,
    isCurrentBillingPlanTrial,
    showAnnualPlans,
    targetBillingPlanId,
    subscriptionData?.recommendedBillingPlanIds,
    referencePlans,
  ]);
};
